import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { configuracionSistemaNormalize } from '../../../Objetos/ConfiguracionesSistemaModel';
import { normalizeValue } from '../../../../../utils/Texts';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from './ConfiguracionesSistemaModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import '../../../Styles/ColoresCatalog.css'; // Reutilizamos estilos
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';
import { getSessionPerfilId } from '../../../../../utils/SessionUser';

const PAGE_SIZE = 12;
const endpoint = ENDPOINTS.configuracionesSistema;
const endpointById = ENDPOINTS.configuracionSistemaById;
const modelo = configuracionSistemaNormalize;

export default function ConfiguracionesSistemaCatalog() {
    const idPerfil = getSessionPerfilId();
    const isSuperAdmin = Number(idPerfil) === 1;
    const { puedeEditar, puedeEliminar } = useResourcePermissions('Configuraciones Sistema');
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [editData, setEditData] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const editorRef = useRef(null);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(endpoint);
            const data = (res.data || []).map((x) => modelo(x));
            setRows(data);
        } catch (error) {
            console.error('Error al cargar configuraciones:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((e) => {
            return normalizeValue(e.nombre).toLowerCase().includes(q) ||
                normalizeValue(e.descripcion).toLowerCase().includes(q) ||
                normalizeValue(e.valor).toLowerCase().includes(q);
        });
    }, [rows, query]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
    };

    const handleEdit = (item) => {
        setSelectedId(item.idConfiguracion);
        setEditData(item);
        setModalMode('edit');
        setModalOpen(true);
    };

    const handleSubmit = async (form, mode) => {
        try {
            setLoading(true);
            const payload = {
                IdConfiguracion: mode === 'new' ? 0 : selectedId,
                Nombre: form.nombre,
                Descripcion: form.descripcion,
                Valor: form.valor,
                Activo: form.activo
            };

            if (mode === 'new') {
                await axios.post(endpoint, payload);
            } else {
                await axios.put(endpoint, payload);
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    if (!isSuperAdmin) {
        return (
            <div className="panel-sigma-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc' }}>
                <div style={{ maxWidth: '400px', backgroundColor: '#ffffff', padding: '2.5rem 2rem', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)', textAlign: 'center', margin: '4rem auto' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#fee2e2', color: '#ef4444', marginBottom: '1.5rem' }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                    </div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>
                        Acceso Restringido
                    </h2>
                    <p style={{ color: '#4b5563', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                        Lo sentimos, no podemos cargar este módulo porque tu usuario no cuenta con la configuración necesaria.
                    </p>
                    <div style={{ textAlign: 'left', backgroundColor: '#f9fafb', padding: '1rem 1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                        <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginTop: 0, marginBottom: '0.5rem' }}>
                            Detalles del error:
                        </p>
                        <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', color: '#ef4444' }}>
                            <li style={{ marginBottom: '0.25rem' }}>Se requieren privilegios de Super Administrador.</li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">Configuraciones de Sistema</h2>
                    <input
                        className="roles-search"
                        placeholder="Buscar configuración..."
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Nombre (Clave)</th>
                                    <th>Descripción</th>
                                    <th>Valor</th>
                                    {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                                    {/* {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>} */}
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => (
                                    <tr key={r.idConfiguracion}>
                                        <td style={{ fontWeight: 600 }}>{r.nombre}</td>
                                        <td>{r.descripcion}</td>
                                        <td style={{
                                            fontFamily: '"Fira Code", monospace',
                                            fontSize: '0.85rem',
                                            maxWidth: '300px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }} title={r.nombre === 'CORREO_NOTIFICACIONES_CLAVE' ? 'Contraseña Oculta' : r.valor}>
                                            {r.nombre === 'CORREO_NOTIFICACIONES_CLAVE' ? '••••••••••••' : r.valor}
                                        </td>
                                        {puedeEditar && (
                                            <td style={{ textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    className="rk-iconbtn rk-iconbtn--edit"
                                                    onClick={() => handleEdit(r)}
                                                    aria-label="Editar"
                                                    title="Editar"
                                                >
                                                    <img src={sigmaEdit} alt="" className="rk-ico" />
                                                </button>
                                            </td>
                                        )}
                                        {/* {puedeEliminar && (
                                            <td style={{ textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    className="rk-iconbtn rk-iconbtn--del"
                                                    onClick={() => askDelete(r.idConfiguracion, r.nombre)}
                                                    aria-label="Borrar"
                                                    title="Borrar"
                                                >
                                                    <img src={sigmaDelete} alt="" className="rk-ico" />
                                                </button>
                                            </td>
                                        )} */}
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (
                                    <tr><td colSpan={3 + (puedeEditar ? 1 : 0) /* + (puedeEliminar ? 1 : 0) */} style={{ textAlign: 'center', padding: '1rem' }}>Sin resultados encontrados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                    </div>
                    <div className="roles-pagination-right">
                        <button className={`roles-page-btn ${page === 1 ? 'is-disabled' : ''}`} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                            <button key={n} className={`roles-page-btn ${n === page ? 'is-active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                        ))}
                        <button className={`roles-page-btn ${page === totalPages ? 'is-disabled' : ''}`} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
                    </div>
                </div>
            </div>

            {modalOpen && (
                <div className="roles-editor-slot" ref={editorRef}>
                    <Modal
                        open={modalOpen}
                        mode={modalMode}
                        data={editData}
                        onClose={() => setModalOpen(false)}
                        onSubmit={handleSubmit}
                        inline={true}
                        triggerAlert={triggerAlert}
                    />
                </div>
            )}

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
